'use client';

import { invalidateProjectMetadata } from './projectSync';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { REALTIME_EVENT_NAMES, type RealtimeEventName } from '@agentteams/realtime-contract';
import { fetchWithAuthRetry, getApiUrl } from '../lib/api/common';
import { authSessionChangedEventName, getAuthSession } from '../lib/authSession';
import { hasDesktopBridge } from '../lib/desktopBridge';
import { getRealtimeClientIdentity, type RealtimeClientIdentity } from '../lib/realtimeClientIdentity';
import { realtimeReconnectRequestEventName } from '../lib/realtimeReconnect';
import { queryKeys } from '../hooks/queries/queryKeys';
import { useRealtimeStatusStore, type RealtimeConnectionStatus } from '../stores/realtimeStatusStore';
import { toDesktopNotificationPayload } from './DesktopBridgeProvider';
import {
  getStringField,
  getUnreadCountFromPayload,
  parseRealtimePayload,
  shouldShowDesktopNotification,
} from './realtimePayload';
import {
  type BadgeFieldSnapshots,
  resolveDaemonTriggerInvalidation,
  resolvePlanInvalidation,
  resolveProjectDomainInvalidation,
} from './realtimeInvalidation';

const INITIAL_RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_DELAY_MS = 60_000;
const RECONNECT_JITTER_MS = 500;
// 단발성 끊김(네트워크 블립)엔 배너를 띄우지 않고, 재연결이 이만큼 연속 실패해 실시간이 지연될
// 가능성이 높을 때만 "unstable"로 전환한다(2회 ≈ 2s+4s backoff). 깜빡임 방지.
const UNSTABLE_AFTER_ATTEMPTS = 2;
const DAEMON_TRIGGER_INVALIDATION_DELAY_MS = 3_000;
const HOME_INVALIDATION_DELAY_MS = 2_500;
const PROJECT_INVALIDATION_DELAY_MS = 2_500;
const SSE_HEARTBEAT_EVENT_NAME = 'heartbeat';
const SSE_STALE_AFTER_MS = 75_000;
const SSE_WATCHDOG_INTERVAL_MS = 15_000;
const FORCE_RECONNECT_DEBOUNCE_MS = 500;
// 백그라운드(숨김)에 이 시간 이상 머문 뒤 복귀하면, 일시정지 동안 끊긴 SSE로 놓친 변화가 있다고
// 보고 active 쿼리를 한 번 강제 refetch한다. iOS 홈앱(standalone)이 suspend됐다 돌아오는 경우가
// 대표 사례. 짧은 데스크탑 탭 전환(수 초)은 임계값 미만이라 그대로 두어 멀티탭 처닝을 막는다.
const RESUME_REFETCH_AFTER_HIDDEN_MS = 30_000;
// 같은 브라우저 프로필/데스크탑 앱 내 탭·뷰 간 leader/follower 조정 채널. 데스크탑·일반 웹이
// 동일 코드 경로를 쓰도록 단일 채널 이름을 공유한다(BroadcastChannel은 origin/프로필 단위라
// 같은 deviceId 그룹과 사실상 일치한다).
const REALTIME_COORDINATION_CHANNEL_NAME = 'agentteams:desktop-realtime-events:v1';
const DESKTOP_SSE_LOG_REQUEST_EVENT = 'agentteams:desktopSseLogRequest';
const DESKTOP_LEADER_HEARTBEAT_MS = 1_000;
const DESKTOP_LEADER_LEASE_MS = 3_000;
const DESKTOP_LEADER_CLAIM_DELAY_MS = 120;

// web이 구독하는 SSE 이벤트명 카탈로그(attach/detach가 이 목록을 단일 출처로 사용한다).
// api emit·desktop 릴레이와 공유하는 단일 계약(@agentteams/realtime-contract)을 재export한다.
// 한쪽만 바뀌면 타입/빌드가 깨지므로 더 이상 문자열 리터럴이 중복되지 않는다.
export { REALTIME_EVENT_NAMES, type RealtimeEventName };

type SseTokenResponse = {
  token: string;
  expiresIn: number;
};

type SseTokenFetchResult = {
  token: string | null;
  retryAfterMs?: number;
};

type DesktopRealtimeMessage =
  | { type: 'hello'; senderId: string }
  | { type: 'claim'; senderId: string }
  | { type: 'heartbeat'; senderId: string; realtimeStatus?: RealtimeConnectionStatus }
  | { type: 'resign'; senderId: string }
  | {
      type: 'event';
      senderId: string;
      eventName: (typeof REALTIME_EVENT_NAMES)[number];
      data: string;
      lastEventId: string;
    };

const createDesktopInstanceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const fetchSseToken = async (accessToken: string, identity: RealtimeClientIdentity): Promise<SseTokenFetchResult> => {
  try {
    // 일반 API와 동일하게 fetchWithAuthRetry를 사용한다. 이 유틸은 (1) access token이
    // 만료됐으면 요청 전에 refresh token으로 선제 갱신하고 (2) 그래도 401이면 갱신 후
    // 1회 재시도하며, 최신 토큰으로 Authorization 헤더를 덮어쓴다. 직접 fetch를 쓰면
    // 데스크탑 앱이 오래 켜져 access token이 만료된 뒤에도 같은 만료 토큰으로 backoff
    // 재시도를 반복해 API에 TokenExpiredError 로그가 계속 쌓인다.
    // device 정보를 함께 보내 서버가 연결 제한을 디바이스 단위로 적용/진단하게 한다.
    const response = await fetchWithAuthRetry(`${getApiUrl()}/api/notifications/stream/token`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId: identity.deviceId, clientKind: identity.clientKind }),
    });
    if (!response.ok) {
      if (response.status === 429) {
        try {
          const body = (await response.json()) as { retryAfter?: unknown };
          const retryAfter =
            typeof body?.retryAfter === 'number' && Number.isFinite(body.retryAfter) ? body.retryAfter : null;
          return {
            token: null,
            ...(retryAfter !== null ? { retryAfterMs: Math.max(0, retryAfter) * 1000 } : {}),
          };
        } catch {
          return { token: null };
        }
      }
      return { token: null };
    }
    const body = (await response.json()) as SseTokenResponse;
    if (typeof body?.token !== 'string' || body.token.length === 0) return { token: null };
    return { token: body.token };
  } catch {
    return { token: null };
  }
};

export function RealtimeEventsProvider(): null {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => getAuthSession()?.token ?? null);

  useEffect(() => {
    const syncToken = (): void => {
      setToken(getAuthSession()?.token ?? null);
    };

    syncToken();
    window.addEventListener(authSessionChangedEventName, syncToken);
    window.addEventListener('storage', syncToken);
    return () => {
      window.removeEventListener(authSessionChangedEventName, syncToken);
      window.removeEventListener('storage', syncToken);
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    if (typeof EventSource === 'undefined') return;

    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let daemonTriggerInvalidationTimer: ReturnType<typeof setTimeout> | null = null;
    let pendingTerminalRunUpdate = false;
    let homeInvalidationTimer: ReturnType<typeof setTimeout> | null = null;
    let projectInvalidationTimer: ReturnType<typeof setTimeout> | null = null;
    const pendingProjectInvalidations = new Map<string, readonly unknown[]>();
    const pendingDaemonTriggerInvalidations = new Map<string, readonly unknown[]>();
    const badgeFieldSnapshots: BadgeFieldSnapshots = new Map();
    let retryDelayMs = INITIAL_RECONNECT_DELAY_MS;
    let cancelled = false;
    let reconnectAttempts = 0;
    const setRealtimeStatus = useRealtimeStatusStore.getState().setStatus;
    const isDesktopSseOwnerMode =
      hasDesktopBridge() &&
      typeof window.agentteamsBridge?.onRealtimeOwnerState === 'function' &&
      typeof window.agentteamsBridge.onRealtimeEvent === 'function';
    // 일반 웹은 BroadcastChannel 조정으로 같은 브라우저 프로필 안의 EventSource를 1개로 줄인다.
    // 데스크탑은 Electron main이 단일 SSE owner 권한을 부여하고 event fan-out을 중계한다.
    const shouldCoordinate = !isDesktopSseOwnerMode && typeof BroadcastChannel !== 'undefined';
    const clientIdentity = getRealtimeClientIdentity();
    const instanceId = shouldCoordinate || isDesktopSseOwnerMode ? createDesktopInstanceId() : '';
    const coordinationChannel = shouldCoordinate ? new BroadcastChannel(REALTIME_COORDINATION_CHANNEL_NAME) : null;
    let isLeader = !shouldCoordinate && !isDesktopSseOwnerMode;
    let lastLeaderHeartbeat = 0;
    let claimTimer: ReturnType<typeof setTimeout> | null = null;
    let leaseTimer: ReturnType<typeof setInterval> | null = null;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let watchdogTimer: ReturnType<typeof setInterval> | null = null;
    let forceReconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let connectSequence = 0;
    let lastSseMessageAt = Date.now();
    let isDesktopBackgrounded = false;
    // 숨김(비활성) 탭 처닝 억제: 탭이 숨겨진 동안 도착한 SSE 무효화는 즉시 refetch하지 않고
    // stale 표시만(refetchType: "none") 한 뒤, 가시화 시점에 1회로 합쳐 refetch한다. 데스크탑은
    // 비활성 탭이 setVisible(false)로 document.visibilityState === "hidden"을 보고하므로(폴링
    // 훅의 refetchIntervalInBackground와 동일 신호) 추가 IPC 없이 같은 게이트가 적용된다.
    let hadDeferredInvalidationWhileHidden = false;
    // 숨김으로 전환된 시각. 복귀 시 백그라운드 체류 시간을 재서 길게 머문 경우만 강제 refetch한다.
    let lastHiddenAt: number | null = null;
    const isDocumentHidden = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';
    // 숨김 상태면 refetch를 미루고(stale만 표시) 가시화 시 합쳐 처리하도록 플래그를 세운다.
    // 보이는 상태면 기존과 동일하게 active 쿼리를 즉시 refetch한다(활성 탭 즉시성 보존).
    const invalidate = (filters: Parameters<typeof queryClient.invalidateQueries>[0]): void => {
      if (isDocumentHidden()) {
        hadDeferredInvalidationWhileHidden = true;
        void queryClient.invalidateQueries({ ...filters, refetchType: 'none' });
        return;
      }
      void queryClient.invalidateQueries(filters);
    };
    let sseStatus = isDesktopSseOwnerMode
      ? 'desktop:follower-waiting'
      : shouldCoordinate
        ? 'coordinated:follower-waiting'
        : 'browser:opening';

    const postDesktopMessage = (message: DesktopRealtimeMessage): void => {
      coordinationChannel?.postMessage(message);
    };

    const postLeaderHeartbeat = (): void => {
      postDesktopMessage({
        type: 'heartbeat',
        senderId: instanceId,
        realtimeStatus: useRealtimeStatusStore.getState().status,
      });
    };

    const reportDesktopSseState = (event: Event): void => {
      if (!hasDesktopBridge()) return;
      const detail =
        event instanceof CustomEvent && typeof event.detail === 'object' && event.detail !== null
          ? (event.detail as Record<string, unknown>)
          : {};
      const lastLeaderHeartbeatAgeMs =
        shouldCoordinate && !isLeader && lastLeaderHeartbeat > 0 ? Date.now() - lastLeaderHeartbeat : null;

      window.agentteamsBridge?.request('realtime:sse-log', {
        ...detail,
        instanceId,
        role: shouldCoordinate || isDesktopSseOwnerMode ? (isLeader ? 'leader' : 'follower') : 'browser',
        status: sseStatus,
        hasEventSource: source !== null,
        isDesktopCoordinated: shouldCoordinate || isDesktopSseOwnerMode,
        lastLeaderHeartbeatAgeMs,
      });
    };

    window.addEventListener(DESKTOP_SSE_LOG_REQUEST_EVENT, reportDesktopSseState);

    const markSseMessageReceived = (): void => {
      lastSseMessageAt = Date.now();
    };

    const onNotification = (event: MessageEvent): void => {
      try {
        const payload = parseRealtimePayload(event);
        const unreadCount = getUnreadCountFromPayload(payload);

        if (unreadCount !== null) {
          // 배지/unread 카운트는 fetch 없이 캐시에 직접 반영하므로 숨김 탭에서도 즉시 신선하다.
          queryClient.setQueryData(queryKeys.notifications.unified.unreadCount, unreadCount);
        }
        // 알림 목록 refetch만 가시성 게이팅 대상(카운트 신선도는 위 setQueryData로 보존).
        invalidate({ queryKey: queryKeys.notifications.unified.all });

        if (!hasDesktopBridge()) return;
        if (shouldCoordinate && !isLeader) return;
        if (!shouldShowDesktopNotification(payload)) return;

        const desktopPayload = toDesktopNotificationPayload(payload);
        if (desktopPayload) {
          window.agentteamsBridge?.request('notification:show', desktopPayload);
        }
      } catch {
        // 잘못된 페이로드는 silently 무시
      }
    };

    const invalidateHomeQueries = (): void => {
      if (homeInvalidationTimer !== null) return;

      homeInvalidationTimer = setTimeout(() => {
        homeInvalidationTimer = null;
        // 도메인 이벤트(plan/report/postMortem/coAction/codeReview/document/convention)는
        // 홈 피드와 액션필요 위젯만 실제로 바뀐다. me.all로 전체를 무효화하면 변동이 드물고
        // 무거운 위젯(활동 히트맵=무제한 조회, 프로젝트 목록)까지 2.5초 주기로 재요청되어
        // 활성 워크스페이스에서 부하가 곱연산으로 증폭된다. 최근 실행 목록은 daemonTrigger
        // 이벤트가 active/past 그룹만 정밀 무효화하므로 여기서 제외한다.
        invalidate({ queryKey: queryKeys.me.feedAll });
        // projectIds 없이 호출 → prefix(["me","actionRequired"])로 selection 붙은 키까지 무효화.
        invalidate({ queryKey: queryKeys.me.actionRequired() });
      }, HOME_INVALIDATION_DELAY_MS);
    };

    // 활성 러너가 도메인 이벤트(plan.updated 등)를 연속 발행하면 매 이벤트마다 즉시
    // 무효화할 경우 열린 상세 패널의 쿼리(usePlanDetail 등)가 폭주
    // refetch되어 멤버별 rate-limit을 소진시킨다. 같은 queryKey 무효화는 창 안에서 dedupe하고
    // 창이 끝날 때 한 번만 발화하도록 coalescing한다(home/daemon 무효화와 동일한 정책).
    const enqueueProjectInvalidation = (queryKey: readonly unknown[]): void => {
      pendingProjectInvalidations.set(JSON.stringify(queryKey), queryKey);
      if (projectInvalidationTimer !== null) return;

      projectInvalidationTimer = setTimeout(() => {
        projectInvalidationTimer = null;
        const keysToInvalidate = [...pendingProjectInvalidations.values()];
        pendingProjectInvalidations.clear();
        for (const queryKey of keysToInvalidate) {
          invalidate({ queryKey });
        }
      }, PROJECT_INVALIDATION_DELAY_MS);
    };

    const invalidateProjectQueries = (
      event: Event,
      queryKeyForProject: (projectId: string) => readonly unknown[],
      affectsNavigationBadges = false,
    ): void => {
      invalidateHomeQueries();
      if (!('data' in event)) return;

      try {
        const payload = parseRealtimePayload(event as MessageEvent);
        const queryKey = resolveProjectDomainInvalidation(payload, queryKeyForProject);
        if (!queryKey) return;

        enqueueProjectInvalidation(queryKey);
        if (affectsNavigationBadges) {
          const projectId = getStringField(payload, 'projectId');
          if (projectId) enqueueProjectInvalidation(queryKeys.navigationBadges.project(projectId));
        }
      } catch {
        // 잘못된 페이로드여도 홈 피드 갱신은 유지하고 도메인 갱신만 건너뛴다.
      }
    };

    // plan.updated는 다른 도메인과 달리 plans.all 전체를 무효화하지 않는다. plans.all은
    // 열린 상세 패널의 fullDetail까지 매 이벤트마다 refetch시켜
    // 멤버 rate-limit(분당 100)을 소진시키는 폭주원이었다. 리스트/무한스크롤 키만 갱신하고,
    // 상세는 실제 업데이트된 planId만 정밀 무효화한다.
    const invalidatePlanQueries = (event: Event): void => {
      invalidateHomeQueries();
      if (!('data' in event)) return;

      try {
        const payload = parseRealtimePayload(event as MessageEvent);
        const { keys } = resolvePlanInvalidation(payload, badgeFieldSnapshots);
        for (const queryKey of keys) {
          enqueueProjectInvalidation(queryKey);
        }
      } catch {
        // 잘못된 페이로드여도 홈 피드 갱신은 유지한다.
      }
    };

    const invalidateCompletionReportQueries = (event: Event): void => {
      invalidateProjectQueries(event, queryKeys.completionReports.all, true);
    };

    const invalidatePostMortemQueries = (event: Event): void => {
      invalidateProjectQueries(event, queryKeys.postMortems.all, true);
    };

    const invalidateCoActionQueries = (event: Event): void => {
      invalidateProjectQueries(event, queryKeys.coActions.all, true);
    };

    const invalidateCodeReviewQueries = (event: Event): void => {
      invalidateProjectQueries(event, queryKeys.codeReviews.all, true);
    };

    const invalidateDocumentQueries = (event: Event): void => {
      invalidateProjectQueries(event, queryKeys.documents.all);
    };

    const invalidateConventionQueries = (event: Event): void => {
      invalidateProjectQueries(event, queryKeys.conventions.all);
    };

    const invalidateSkillQueries = (event: Event): void => {
      invalidateProjectQueries(event, queryKeys.skills.all);
    };

    const invalidateDaemonTriggerQueries = (event: Event): void => {
      // 페이로드 status가 terminal이면 past 목록도 한 번 갱신하도록 플래그를 세운다.
      // (timer가 이미 떠 있어도 status 검사는 매 이벤트 수행해 창 내 terminal 전환을 놓치지 않는다.)
      if ('data' in event) {
        try {
          const payload = parseRealtimePayload(event as MessageEvent);
          const invalidation = resolveDaemonTriggerInvalidation(payload, badgeFieldSnapshots);
          for (const queryKey of invalidation.keys) {
            pendingDaemonTriggerInvalidations.set(JSON.stringify(queryKey), queryKey);
          }
          if (invalidation.includeTerminal) {
            pendingTerminalRunUpdate = true;
          }
        } catch {
          // 페이로드 파싱 실패는 무시(active 갱신은 유지).
        }
      }

      if (daemonTriggerInvalidationTimer !== null) return;

      daemonTriggerInvalidationTimer = setTimeout(() => {
        daemonTriggerInvalidationTimer = null;
        const includeTerminal = pendingTerminalRunUpdate;
        pendingTerminalRunUpdate = false;
        invalidate({ queryKey: queryKeys.me.recentRunsByGroup('active') });
        const keysToInvalidate =
          pendingDaemonTriggerInvalidations.size > 0
            ? [...pendingDaemonTriggerInvalidations.values()]
            : [queryKeys.daemonTriggers.lists, queryKeys.daemonTriggers.infinites];
        pendingDaemonTriggerInvalidations.clear();
        for (const queryKey of keysToInvalidate) {
          invalidate({ queryKey });
        }
        if (includeTerminal) {
          invalidate({ queryKey: queryKeys.me.recentRunsByGroup('past') });
        }
      }, DAEMON_TRIGGER_INVALIDATION_DELAY_MS);
    };

    // 카탈로그(REALTIME_EVENT_NAMES)를 단일 출처로 attach/detach가 같은 핸들러 매핑을
    // 사용한다. 이벤트명이 추가/오타나면 핸들러 매핑이 비어 컴파일/테스트에서 드러난다.
    const invalidateProjectMetadataQueries = (event: MessageEvent): void => {
      try {
        const payload = parseRealtimePayload(event);
        const projectId = getStringField(payload, 'projectId');
        if (!projectId && getStringField(payload, 'source') !== 'connection') return;
        const hidden = isDocumentHidden();
        if (hidden) hadDeferredInvalidationWhileHidden = true;
        void invalidateProjectMetadata(queryClient, projectId ?? undefined, hidden ? 'none' : 'active');
      } catch {
        // 손상된 이벤트는 다음 연결 복구에서 다시 조회한다.
      }
    };

    const eventListeners: ReadonlyArray<
      readonly [(typeof REALTIME_EVENT_NAMES)[number], (event: MessageEvent) => void]
    > = [
      ['notification', onNotification],
      ['plan.updated', invalidatePlanQueries],
      ['completionReport.updated', invalidateCompletionReportQueries],
      ['postMortem.updated', invalidatePostMortemQueries],
      ['coAction.updated', invalidateCoActionQueries],
      ['codeReview.updated', invalidateCodeReviewQueries],
      ['document.updated', invalidateDocumentQueries],
      ['convention.updated', invalidateConventionQueries],
      ['daemonTrigger.updated', invalidateDaemonTriggerQueries],
      ['skill.updated', invalidateSkillQueries],
      ['project.updated', invalidateProjectMetadataQueries],
    ];

    const onRealtimeEvent = (eventName: (typeof REALTIME_EVENT_NAMES)[number], event: MessageEvent): void => {
      markSseMessageReceived();
      const handler = eventListeners.find(([name]) => name === eventName)?.[1];
      if (!handler) return;
      handler(event);

      if (!isLeader) return;
      const data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
      if (isDesktopSseOwnerMode) {
        window.agentteamsBridge?.request('realtime:sse-event', {
          instanceId,
          eventName,
          data,
          lastEventId: event.lastEventId,
        });
        return;
      }
      if (shouldCoordinate) {
        postDesktopMessage({
          type: 'event',
          senderId: instanceId,
          eventName,
          data,
          lastEventId: event.lastEventId,
        });
      }
    };

    const browserEventListeners = eventListeners.map(
      ([name]) => [name, (event: MessageEvent) => onRealtimeEvent(name, event)] as const,
    );

    const isDesktopRealtimePaused = (): boolean => isDesktopSseOwnerMode && isDesktopBackgrounded;

    const onHeartbeat = (): void => {
      markSseMessageReceived();
    };

    const detachListeners = (target: EventSource): void => {
      for (const [name, handler] of browserEventListeners) {
        target.removeEventListener(name, handler);
      }
      target.removeEventListener(SSE_HEARTBEAT_EVENT_NAME, onHeartbeat);
    };

    const scheduleReconnect = (minimumDelayMs: number = 0): void => {
      if (cancelled || reconnectTimer !== null) return;
      if (isDesktopRealtimePaused()) return;
      reconnectAttempts += 1;
      sseStatus = 'reconnecting';
      if (reconnectAttempts >= UNSTABLE_AFTER_ATTEMPTS) {
        // 재연결이 연속 실패 중 → 실시간 지연 가능. 상단 배너로 사용자에게 알린다.
        setRealtimeStatus('unstable');
      }
      const baseDelay = Math.max(minimumDelayMs, Math.min(retryDelayMs, MAX_RECONNECT_DELAY_MS));
      const jitter = Math.random() * RECONNECT_JITTER_MS;
      retryDelayMs = Math.min(retryDelayMs * 2, MAX_RECONNECT_DELAY_MS);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (isLeader) {
          void connect();
        }
      }, baseDelay + jitter);
    };

    const closeSource = (): void => {
      if (!source) return;
      detachListeners(source);
      source.close();
      source = null;
    };

    const clearReconnectTimer = (): void => {
      if (reconnectTimer === null) return;
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    };

    const clearForceReconnectTimer = (): void => {
      if (forceReconnectTimer === null) return;
      clearTimeout(forceReconnectTimer);
      forceReconnectTimer = null;
    };

    const stopDesktopHeartbeat = (): void => {
      if (heartbeatTimer === null) return;
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    };

    const resignDesktopLeadership = (): void => {
      if (!isLeader) return;
      isLeader = false;
      sseStatus = isDesktopSseOwnerMode ? 'desktop:follower' : 'coordinated:follower';
      postDesktopMessage({ type: 'resign', senderId: instanceId });
      if (isDesktopSseOwnerMode) {
        window.agentteamsBridge?.request('realtime:sse-owner-release', { instanceId });
      }
      stopDesktopHeartbeat();
      clearReconnectTimer();
      closeSource();
    };

    const connect = async (): Promise<void> => {
      if (cancelled) return;
      if (!isLeader) return;
      if (isDesktopRealtimePaused()) return;
      const connectId = ++connectSequence;
      sseStatus = isDesktopSseOwnerMode
        ? 'desktop:owner-token'
        : shouldCoordinate
          ? 'coordinated:leader-token'
          : 'browser:token';

      const sseTokenResult = await fetchSseToken(token, clientIdentity);
      if (cancelled) return;
      if (!isLeader) return;
      if (isDesktopRealtimePaused()) return;
      if (connectId !== connectSequence) return;
      if (!sseTokenResult.token) {
        // 토큰 발급 실패는 일시 오류로 간주하고 backoff 후 재시도한다.
        scheduleReconnect(sseTokenResult.retryAfterMs);
        return;
      }

      const url = `${getApiUrl()}/api/notifications/stream?token=${encodeURIComponent(sseTokenResult.token)}`;
      closeSource();
      const next = new EventSource(url);
      source = next;
      markSseMessageReceived();
      sseStatus = isDesktopSseOwnerMode
        ? 'desktop:owner-connecting'
        : shouldCoordinate
          ? 'coordinated:leader-connecting'
          : 'browser:connecting';

      next.addEventListener('open', () => {
        retryDelayMs = INITIAL_RECONNECT_DELAY_MS;
        reconnectAttempts = 0;
        sseStatus = isDesktopSseOwnerMode
          ? 'desktop:owner-connected'
          : shouldCoordinate
            ? 'coordinated:leader-connected'
            : 'browser:connected';
        setRealtimeStatus('connected');
        // SSE에는 재생 로그가 없으므로 연결할 때마다 모든 탭에 기준정보 재조회 신호를 전달한다.
        onRealtimeEvent(
          'project.updated',
          new MessageEvent('project.updated', {
            data: JSON.stringify({ payload: { source: 'connection' } }),
          }),
        );
      });

      next.addEventListener('error', () => {
        // 401/429 등을 포함한 오류 발생 시 native auto-reconnect 폭주를 막기 위해
        // 명시적으로 close하고 backoff 스케줄링한다.
        detachListeners(next);
        next.close();
        if (source === next) source = null;
        sseStatus = 'error';
        scheduleReconnect();
      });

      for (const [name, handler] of browserEventListeners) {
        next.addEventListener(name, handler);
      }
      next.addEventListener(SSE_HEARTBEAT_EVENT_NAME, onHeartbeat);
    };

    const forceReconnect = (): void => {
      if (cancelled || !isLeader) return;
      if (isDesktopRealtimePaused()) return;
      clearForceReconnectTimer();
      clearReconnectTimer();
      connectSequence += 1;
      retryDelayMs = INITIAL_RECONNECT_DELAY_MS;
      reconnectAttempts = 0;
      markSseMessageReceived();
      closeSource();
      void connect();
    };

    const scheduleForceReconnect = (): void => {
      if (cancelled || !isLeader || forceReconnectTimer !== null) return;
      if (isDesktopRealtimePaused()) return;
      forceReconnectTimer = setTimeout(() => {
        forceReconnectTimer = null;
        forceReconnect();
      }, FORCE_RECONNECT_DEBOUNCE_MS);
    };

    const handleRealtimeReconnectRequest = (): void => {
      scheduleForceReconnect();
    };

    // 탭이 다시 보이면, 숨김 동안 미뤄둔 무효화를 1회의 refetch로 합쳐 처리한다. stale 표시된
    // active 쿼리만 다시 받으므로(이미 fresh한 건 그대로) 탭 수에 비례하던 처닝이 가시 탭 1회로
    // 수렴한다. refetchOnWindowFocus는 전역 비활성이라 이 명시적 합류가 그 역할을 대신한다.
    const flushDeferredInvalidations = (): void => {
      if (isDocumentHidden()) return;
      if (!hadDeferredInvalidationWhileHidden) return;
      hadDeferredInvalidationWhileHidden = false;
      void queryClient.refetchQueries({ type: 'active', stale: true });
    };

    // 백그라운드에 오래 머문 뒤 복귀하면, SSE-deferred 무효화 유무와 무관하게 active 쿼리를 한 번
    // refetch한다. iOS 홈앱처럼 suspend되어 SSE가 끊겼던 경우엔 deferred 플래그가 서지 않아
    // flushDeferredInvalidations만으로는 화면이 낡은 채 남기 때문이다. 기존 데이터를 유지한 채
    // 백그라운드 갱신이라(isLoading이 아닌 data 유무로 렌더) 깜빡임은 생기지 않는다.
    const refetchAfterLongBackground = (): void => {
      if (isDocumentHidden()) return;
      if (lastHiddenAt === null) return;
      const hiddenForMs = Date.now() - lastHiddenAt;
      lastHiddenAt = null;
      if (hiddenForMs < RESUME_REFETCH_AFTER_HIDDEN_MS) return;
      void queryClient.refetchQueries({ type: 'active', stale: true });
    };

    const handleAppResume = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        lastHiddenAt = Date.now();
        return;
      }
      flushDeferredInvalidations();
      refetchAfterLongBackground();
      scheduleForceReconnect();
    };

    const handleOnline = (): void => {
      scheduleForceReconnect();
    };

    const startWatchdog = (): void => {
      if (watchdogTimer !== null) return;
      watchdogTimer = setInterval(() => {
        if (cancelled || !isLeader || source === null) return;
        if (isDesktopRealtimePaused()) return;
        if (Date.now() - lastSseMessageAt >= SSE_STALE_AFTER_MS) {
          forceReconnect();
        }
      }, SSE_WATCHDOG_INTERVAL_MS);
    };

    const stopWatchdog = (): void => {
      if (watchdogTimer === null) return;
      clearInterval(watchdogTimer);
      watchdogTimer = null;
    };

    window.addEventListener(realtimeReconnectRequestEventName, handleRealtimeReconnectRequest);
    window.addEventListener('visibilitychange', handleAppResume);
    window.addEventListener('pageshow', handleAppResume);
    window.addEventListener('focus', handleAppResume);
    window.addEventListener('online', handleOnline);
    startWatchdog();

    const becomeDesktopLeader = (): void => {
      if (cancelled || isLeader) return;
      isLeader = true;
      sseStatus = isDesktopSseOwnerMode ? 'desktop:owner-claiming' : 'coordinated:leader-claiming';
      retryDelayMs = INITIAL_RECONNECT_DELAY_MS;
      reconnectAttempts = 0;
      if (shouldCoordinate) {
        postLeaderHeartbeat();
        heartbeatTimer = setInterval(() => {
          postLeaderHeartbeat();
        }, DESKTOP_LEADER_HEARTBEAT_MS);
      }
      void connect();
    };

    const requestDesktopSseOwnership = (): void => {
      if (!isDesktopSseOwnerMode || cancelled || isLeader || claimTimer !== null) return;
      if (isDesktopRealtimePaused()) return;
      sseStatus = 'desktop:claiming';
      window.agentteamsBridge?.request('realtime:sse-owner-claim', { instanceId });
      claimTimer = setTimeout(() => {
        claimTimer = null;
        if (!isLeader) {
          requestDesktopSseOwnership();
        }
      }, DESKTOP_LEADER_LEASE_MS);
    };

    const scheduleDesktopLeadershipClaim = (): void => {
      if (!shouldCoordinate || cancelled || isLeader || claimTimer !== null) {
        return;
      }
      postDesktopMessage({ type: 'claim', senderId: instanceId });
      sseStatus = 'coordinated:claiming';
      claimTimer = setTimeout(() => {
        claimTimer = null;
        const leaseExpired = Date.now() - lastLeaderHeartbeat > DESKTOP_LEADER_LEASE_MS;
        if (leaseExpired) {
          becomeDesktopLeader();
        }
      }, DESKTOP_LEADER_CLAIM_DELAY_MS);
    };

    let offDesktopOwnerState: (() => void) | undefined;
    let offDesktopRealtimeEvent: (() => void) | undefined;
    let offDesktopLifecycleState: (() => void) | undefined;

    if (isDesktopSseOwnerMode) {
      offDesktopLifecycleState = window.agentteamsBridge?.onLifecycleState?.((payload) => {
        if (payload.state === 'background') {
          isDesktopBackgrounded = true;
          lastHiddenAt = Date.now();
          if (claimTimer !== null) {
            clearTimeout(claimTimer);
            claimTimer = null;
          }
          clearReconnectTimer();
          clearForceReconnectTimer();
          stopWatchdog();
          if (isLeader) {
            resignDesktopLeadership();
          } else {
            closeSource();
          }
          sseStatus = 'desktop:background';
          return;
        }

        isDesktopBackgrounded = false;
        startWatchdog();
        flushDeferredInvalidations();
        refetchAfterLongBackground();
        requestDesktopSseOwnership();
      });
      offDesktopOwnerState = window.agentteamsBridge?.onRealtimeOwnerState?.((payload) => {
        if (payload.instanceId && payload.instanceId !== instanceId) return;
        if (payload.granted && claimTimer !== null) {
          clearTimeout(claimTimer);
          claimTimer = null;
        }
        if (payload.granted) {
          if (isDesktopRealtimePaused()) {
            window.agentteamsBridge?.request('realtime:sse-owner-release', { instanceId });
            return;
          }
          becomeDesktopLeader();
          return;
        }
        if (isLeader) {
          resignDesktopLeadership();
        }
      });
      offDesktopRealtimeEvent = window.agentteamsBridge?.onRealtimeEvent?.((payload) => {
        if (isLeader) return;
        const eventName = REALTIME_EVENT_NAMES.find((name) => name === payload.eventName);
        if (!eventName) return;
        onRealtimeEvent(
          eventName,
          new MessageEvent(eventName, {
            data: payload.data,
            lastEventId: payload.lastEventId,
          }),
        );
      });
      requestDesktopSseOwnership();
    } else if (coordinationChannel) {
      coordinationChannel.onmessage = (event: MessageEvent<DesktopRealtimeMessage>) => {
        const message = event.data;
        if (!message || message.senderId === instanceId) return;

        if (message.type === 'hello') {
          if (isLeader) {
            postLeaderHeartbeat();
          }
          return;
        }

        if (message.type === 'claim') {
          if (message.senderId < instanceId) {
            if (claimTimer !== null) {
              clearTimeout(claimTimer);
              claimTimer = null;
            }
            resignDesktopLeadership();
          }
          return;
        }

        if (message.type === 'heartbeat') {
          lastLeaderHeartbeat = Date.now();
          if (claimTimer !== null) {
            clearTimeout(claimTimer);
            claimTimer = null;
          }
          if (message.senderId < instanceId) {
            resignDesktopLeadership();
          }
          if (!isLeader) {
            sseStatus = 'coordinated:follower';
          }
          if (message.realtimeStatus) {
            setRealtimeStatus(message.realtimeStatus);
          }
          return;
        }

        if (message.type === 'resign') {
          if (!isLeader) {
            lastLeaderHeartbeat = 0;
            scheduleDesktopLeadershipClaim();
          }
          return;
        }

        if (message.type === 'event' && !isLeader) {
          onRealtimeEvent(
            message.eventName,
            new MessageEvent(message.eventName, {
              data: message.data,
              lastEventId: message.lastEventId,
            }),
          );
        }
      };

      lastLeaderHeartbeat = 0;
      postDesktopMessage({ type: 'hello', senderId: instanceId });
      scheduleDesktopLeadershipClaim();
      leaseTimer = setInterval(() => {
        if (!isLeader && Date.now() - lastLeaderHeartbeat > DESKTOP_LEADER_LEASE_MS) {
          scheduleDesktopLeadershipClaim();
        }
      }, DESKTOP_LEADER_HEARTBEAT_MS);
    } else {
      void connect();
    }

    return () => {
      cancelled = true;
      // 토큰 변경/로그아웃/언마운트 시 배너가 남지 않도록 상태를 초기화한다.
      setRealtimeStatus('connected');
      window.removeEventListener(DESKTOP_SSE_LOG_REQUEST_EVENT, reportDesktopSseState);
      window.removeEventListener(realtimeReconnectRequestEventName, handleRealtimeReconnectRequest);
      window.removeEventListener('visibilitychange', handleAppResume);
      window.removeEventListener('pageshow', handleAppResume);
      window.removeEventListener('focus', handleAppResume);
      window.removeEventListener('online', handleOnline);
      if (claimTimer !== null) {
        clearTimeout(claimTimer);
        claimTimer = null;
      }
      if (leaseTimer !== null) {
        clearInterval(leaseTimer);
        leaseTimer = null;
      }
      stopDesktopHeartbeat();
      stopWatchdog();
      clearReconnectTimer();
      clearForceReconnectTimer();
      if (isDesktopSseOwnerMode) {
        window.agentteamsBridge?.request('realtime:sse-owner-release', { instanceId });
      }
      offDesktopOwnerState?.();
      offDesktopRealtimeEvent?.();
      offDesktopLifecycleState?.();
      if (daemonTriggerInvalidationTimer !== null) {
        clearTimeout(daemonTriggerInvalidationTimer);
        daemonTriggerInvalidationTimer = null;
      }
      if (homeInvalidationTimer !== null) {
        clearTimeout(homeInvalidationTimer);
        homeInvalidationTimer = null;
      }
      if (projectInvalidationTimer !== null) {
        clearTimeout(projectInvalidationTimer);
        projectInvalidationTimer = null;
      }
      pendingProjectInvalidations.clear();
      pendingDaemonTriggerInvalidations.clear();
      if (isLeader) {
        postDesktopMessage({ type: 'resign', senderId: instanceId });
      }
      closeSource();
      coordinationChannel?.close();
    };
  }, [queryClient, token]);

  return null;
}
